/**
 * TRACI-436: per-browser pick of which Claude model the agent uses. Set from Dev
 * Settings, stored in localStorage, and sent as X-Agent-Model on every request built
 * by getRequestHeaders(), so only this tester's own turns change.
 *
 * The backend is the boundary: it honors the header only on dev, QA and local
 * (shared/agent_models.py), and the agent checks again. The frontend still never
 * sends it from a prod build, and only sends a known key (never a raw model id).
 */
import { isProdBuild } from './handoffProviderOverride';

export const AGENT_MODEL_STORAGE_KEY = 'traci_dev_agent_model';
export const AGENT_MODEL_HEADER = 'X-Agent-Model';

export type AgentModel = 'sonnet-4-6' | 'sonnet-5-5' | 'haiku-5-5';

const MODELS: readonly AgentModel[] = ['sonnet-4-6', 'sonnet-5-5', 'haiku-5-5'];

/** Keep in step with MODEL_CHOICES in shared/agent_models.py. */
export const AGENT_MODEL_LABELS: Record<AgentModel, string> = {
  'sonnet-4-6': 'Sonnet 4.6',
  'sonnet-5-5': 'Sonnet 5.5',
  'haiku-5-5': 'Haiku 5.5',
};

export function isAgentModel(value: unknown): value is AgentModel {
  return typeof value === 'string' && (MODELS as readonly string[]).includes(value);
}

/** The stored pick, or null when unset or not a known model. */
export function getAgentModelOverride(): AgentModel | null {
  try {
    const stored = localStorage.getItem(AGENT_MODEL_STORAGE_KEY);
    return isAgentModel(stored) ? stored : null;
  } catch {
    return null;
  }
}

/** null clears the pick (the environment's default model). */
export function setAgentModelOverride(value: AgentModel | null): void {
  try {
    if (value === null) {
      localStorage.removeItem(AGENT_MODEL_STORAGE_KEY);
    } else {
      localStorage.setItem(AGENT_MODEL_STORAGE_KEY, value);
    }
  } catch { /* storage unavailable: the pick just doesn't stick */ }
}

/** The header to merge into a request; empty in a prod build or with no pick. */
export function agentModelHeaders(): Record<string, string> {
  if (isProdBuild()) return {};
  const model = getAgentModelOverride();
  return model ? { [AGENT_MODEL_HEADER]: model } : {};
}
